import { Badge } from '@/components/ui/badge';
import type { ImplementationTimeDetail } from '@/lib/api/user-activity-details';
import { formatDistanceToNow } from 'date-fns';

interface ImplementationTimeDetailListProps {
  items: ImplementationTimeDetail[];
}

function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export function ImplementationTimeDetailList({ items }: ImplementationTimeDetailListProps) {
  return (
    <ul className="space-y-0" role="list">
      {items.map((item) => (
        <li key={item.id}>
          <ImplementationTimeDetailItem item={item} />
        </li>
      ))}
    </ul>
  );
}

function ImplementationTimeDetailItem({ item }: { item: ImplementationTimeDetail }) {
  const ariaLabel = `Feature ${item.identifier}: ${item.title}. Duration: ${formatDuration(item.durationMinutes)}. Epic: ${item.epicName}. Status: ${item.statusName}. Completed ${formatDistanceToNow(new Date(item.completedAt), { addSuffix: true })}`;

  return (
    <div
      className="border-b py-3 px-3 sm:px-4 hover:bg-muted/50 transition-colors"
      aria-label={ariaLabel}
      role="article"
    >
      <div className="space-y-2">
        <div className="flex items-start sm:items-center gap-2 flex-col sm:flex-row">
          <Badge
            className="text-xs font-mono bg-rose-900/30 text-rose-300 px-2 rounded border-0 flex-shrink-0"
            aria-hidden="true"
          >
            {item.identifier}
          </Badge>
          <span className="font-medium text-sm sm:text-base line-clamp-2 sm:line-clamp-1 sm:truncate">{item.title}</span>
          <Badge
            className="text-xs font-mono bg-rose-900/20 text-rose-200 px-2 rounded border-0 flex-shrink-0 ml-auto"
            aria-hidden="true"
          >
            {formatDuration(item.durationMinutes)}
          </Badge>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-muted-foreground flex-wrap" aria-hidden="true">
          {item.statusColor && (
            <span
              className="inline-block w-2 h-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: item.statusColor }}
            />
          )}
          <span>{item.statusName}</span>
          <span className="hidden sm:inline">•</span>
          <span className="truncate max-w-[150px] sm:max-w-none">{item.epicName}</span>
          <span className="hidden sm:inline">•</span>
          <span className="whitespace-nowrap">{formatDistanceToNow(new Date(item.completedAt), { addSuffix: true })}</span>
        </div>
      </div>
    </div>
  );
}
